import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { VisitorService } from './visitor.service';
import { shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly STORAGE_KEY = 'user';

  private cachedUser: any = null;

  userId: string | null = null;
  sessionId: string | null = null;

  private userSubject = new BehaviorSubject<any | null>(null);
  user$ = this.userSubject.asObservable().pipe(shareReplay(1));

  constructor(private visitorService: VisitorService) {
    this.loadUserFromStorage();
  }

  private isBrowser(): boolean {
    return typeof window !== 'undefined';
  }

  private loadUserFromStorage(): void {
    const user = this.getEncryptedUser();
    this.userSubject.next(user);
    if (user?.userData?.userId) {
      this.userId = user.userData.userId;
    }
  }

  private encrypt(data: any): string {
    return btoa(JSON.stringify(data));
  }

  private decrypt(data: string): any {
    try {
      return JSON.parse(atob(data));
    } catch (error) {
      console.error('Decryption failed', error);
      return null;
    }
  }

  // LocalStorage Helpers
  private getEncryptedUser(): any | null {
    if (this.cachedUser) return this.cachedUser;
    if (!this.isBrowser()) return null;
    const encrypted = localStorage.getItem(this.STORAGE_KEY);
    if (!encrypted) return null;
    
    const user = this.decrypt(encrypted);
    this.cachedUser = user;
    return user;
  }

private saveUser(data: any): void {
  if (!this.isBrowser()) return;
  localStorage.setItem(this.STORAGE_KEY, this.encrypt(data));
  this.cachedUser = data;
  this.userSubject.next(data);
}

  login(username: string, userData: any): boolean {
    if (this.isBrowser() && username) {
      this.saveUser({ username, userData });
      this.userId = userData.userId;
      return true;
    }
    return false;
  }

  logout(): void {
    if (!this.isBrowser()) return;
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem('sessionId');
    this.userId = null;
    this.sessionId = null;
    this.cachedUser = null;
    this.userSubject.next(null);
  }

  isLoggedIn(): boolean {
    return !!this.getEncryptedUser();
  }

  getUserDetails(): { id: string; status: 'loggedin' | 'nonloggedin' } {
    const user = this.getEncryptedUser();
    console.log("User data:", user);
    if (user?.userData?.userId) {
      this.userId = user.userData.userId;
      localStorage.removeItem('sessionId');
    }

    if (!this.userId) {
      this.sessionId = localStorage.getItem('sessionId') || this.generateUUID();
      localStorage.setItem('sessionId', this.sessionId);
      return { id: this.sessionId, status: 'nonloggedin' };
    }

    return { id: this.userId, status: 'loggedin' };
  }

  getDecryptedUser(): any | null {
    return this.getEncryptedUser();
  }

  get accessToken(): string | null {
    return this.getEncryptedUser()?.userData?.accessToken || null;
  }

  getUserPackage(): 'basic' | 'standard' | 'advance' | 'none' | 'pending' {
    if (!this.isBrowser()) return 'none';

    const user = this.getEncryptedUser();
    if (!user) return 'pending';

    const plan = user.userData.plan?.toLowerCase() ?? '';
    const expiry = Number(user.userData.subscriptionExpiresAt ?? 0);
    const now = Date.now();

    if (plan === 'advance' && expiry && now < expiry) {
      return 'advance';
    }

    if (['basic', 'standard', 'advance'].includes(plan)) {
      return plan as 'basic' | 'standard' | 'advance';
    }

    return 'none';
  }

  async getPlanAndDaysLeft(): Promise<{ plan: string, daysLeft: number }> {
    if (this.isLoggedIn()) {
      return new Promise(resolve => {
        setTimeout(() => resolve(this.getTrialLeftDays() ?? { plan: this.getUserPackage(), daysLeft: 0 }), 0);
      });
    }
    return this.visitorService.getSubscriptionInfo();
  }

  getTrialLeftDays(): { plan: string; daysLeft: number } | null {
    const user = this.getEncryptedUser();
    const plan = user?.userData?.plan?.toLowerCase();
    const expiry = Number(user?.userData?.subscriptionExpiresAt || 0);
    const now = Date.now();

    if (expiry > now) {
      const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
      return { plan, daysLeft };
    }

    if (plan === 'advance' && expiry > now) {
      const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
      return { plan, daysLeft };
    }
    return null;
  }

  getUserAllowedStates(): string[] {
    return this.getEncryptedUser()?.userData?.subscribedPreferences?.statePreferences || [];
  }

  generateUUID(): string {
    if (!this.isBrowser()) return '';
    return (1e7 + -1e3 + -4e3 + -8e3 + -1e11).toString().replace(/[018]/g, (c) => {
      const random = crypto.getRandomValues(new Uint8Array(1))[0];
      return (Number(c) ^ (random & 15 >> (Number(c) / 4))).toString(16);
    });
  }

  async shouldShowTrialBanner(): Promise<{ show: boolean, daysLeft: number }> {
    const { plan, daysLeft } = await this.getPlanAndDaysLeft();
    const normalizedPlan = plan.toLowerCase();
  
    const show =
      (normalizedPlan === 'advance' && daysLeft >= 0 && daysLeft <= 5) ||
      normalizedPlan === 'none';
  
    return { show, daysLeft: show ? daysLeft : 0 };
  }

}

